import { IAnalyticEventData } from 'src/types/analytics';
import { Reservation, ReservationItem } from 'src/types';
import { ReservationItemService } from 'src/types/common';

const trackPageView = (
  propertyCode: string,
  page: string,
  event?: IAnalyticEventData | string,
) => {
  if (event === undefined || event === null || event === '') {
    if (page === 'booking-engine/rooms-and-rates') {
      event = 'RoomsAndRates';
    } else if (page === 'booking-engine/customer-information') {
      event = 'CustomerInformation';
    } else if (page === 'booking-engine/payment-details') {
      event = 'PaymentDetails';
    } else {
      event = 'NewPageLoaded';
    }
  }

  const virtualPage = propertyCode + '/' + page;
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      VirtualPage: virtualPage,
      event: event,
      PropertyCode: propertyCode,
      dateTime: new Date(),
    });
  }

  if (window.fbq !== undefined) {
    window.fbq('track', 'ViewContent', { content_name: virtualPage });
  }
};

const trackPromotionView = (promoCode: string) => {
  if (typeof window.dataLayer !== 'undefined' && promoCode != null) {
    window.dataLayer.push({
      VirtualPage: '/booking-engine/PromotionCodeAdded',
      event: 'PromotionCodeAdded',
      dateTime: new Date(),
      ecommerce: {
        promoView: {
          promotions: [
            {
              id: promoCode,
              name: promoCode,
              //'creative': '',
              //'position': ''
            },
          ],
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    window.fbq('track', 'EnterPromoCode', { promotion: promoCode });
  }
};

const trackBookingConfirmation = (
  propertyCode: string,
  currency: string,
  promoCode: string,
  confirmationNumber: string,
  totalCost: number,
  reservation: Reservation,
  reservationItems: Array<ReservationItem>,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const roomData = reservationItems.map((r: ReservationItem) => {
      return {
        sku: r.roomTypeId,
        name: r.roomType.Name,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.dateFrom,
        price: r.totalCost,
        quantity: 1,
        dateFrom: r.dateFrom,
        dateTo: r.dateTo,
      };
    });
    const servicesData = reservationItems
      .flatMap((item) => item.services)
      .map((s: ReservationItemService) => {
        return {
          sku: s.Service.ServiceCode,
          name: s.Service.ServiceName,
          category: 'Service',
          price: s.Service.Price,
          quantity: s.Quantity,
        };
      });

    const transactionProducts = [roomData].push.apply(servicesData);

    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/booking-confirmation',
      event: 'BookingConfirmation',
      transactionId: confirmationNumber,
      transactionAffiliation: propertyCode,
      transactionTotal: totalCost,
      transactionCurrency: currency,
      transactionProducts: transactionProducts,
      promotionCode: promoCode,
      dateTime: new Date(),
    });

    const productData = reservationItems.map((r: ReservationItem) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        price: r.totalCost,
        quantity: 1,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.dateFrom,
        metric1: r.dateFrom,
        metric2: r.dateTo,
      };
    });

    const products = [productData].push.apply(servicesData);

    // For enhanced e-commerce
    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/purchase',
      event: 'Purchase',
      dateTime: new Date(),
      ecommerce: {
        purchase: {
          actionField: {
            id: confirmationNumber,
            affiliation: propertyCode,
            revenue: totalCost,
            coupon: promoCode,
            promotionCode: promoCode,
          },
          products: products,
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    const purchaseRoomData = reservationItems.map((r: ReservationItem) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name: r.roomType.Name,
        category: 'Room',
        variant: 'From Date: ' + r.dateFrom,
        price: r.totalCost,
        dateFrom: r.dateFrom,
        dateTo: r.dateTo,
      };
    });
    const purchaseServicesData = reservationItems
      .flatMap((item) => item.services)
      .map((s: ReservationItemService) => {
        return {
          id: s.Service.ServiceCode,
          quantity: s.Quantity,
          // Custom properties below here:
          name: s.Service.ServiceName,
          category: 'Service',
          price: s.Service.Price,
        };
      });

    const contents = [purchaseRoomData].push.apply(purchaseServicesData);

    const purchaseData = {
      content_name:
        propertyCode + '/booking-engine/booking-confirmation-Purchase',
      dateTime: new Date(),
      value: totalCost,
      currency: currency,
      contents: contents,
      content_type: 'Room',
      // Custom properties below here:
      transactionId: confirmationNumber,
      transactionAffiliation: propertyCode,
      promotionCode: promoCode,
    };

    window.fbq('track', 'Purchase', purchaseData);
  }
};

const trackAddRoomToBasket = (
  propertyCode: string,
  currency: string,
  reservationItem: ReservationItem,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const products = [reservationItem].map((r) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        price: r.totalCost,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.dateFrom,
        metric1: r.dateFrom,
        metric2: r.dateTo,
      };
    });

    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/addRoomToBasket',
      event: 'AddToCart',
      dateTime: new Date(),
      ecommerce: {
        transactionid: reservationItem.roomId,
        currencyCode: currency,
        add: {
          products: products,
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    let totalValue = 0;

    const addToCartProducts = [reservationItem].map((r) => {
      const roomCost = r.totalCost;
      totalValue += roomCost;

      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        price: roomCost,
      };
    });

    const addToCartData = {
      contents: addToCartProducts,
      value: totalValue,
      currency: currency,
    };

    window.fbq('track', 'AddToCart', addToCartData);
  }
};

const trackRemoveRoomFromBasket = (
  propertyCode: string,
  currency: string,
  reservationItem: ReservationItem,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const products = [reservationItem].map((r) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        price: r.totalCost,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.dateFrom,
        metric1: r.dateFrom,
        metric2: r.dateTo,
      };
    });

    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/RemoveFromCart',
      event: 'RemoveFromCart',
      dateTime: new Date(),
      ecommerce: {
        currencyCode: currency,
        remove: {
          products: products,
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    let totalValue = 0;

    const removeFromCartProducts = [reservationItem].map((r) => {
      const roomCost = r.totalCost;
      totalValue += roomCost;

      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        price: roomCost,
      };
    });

    const removeFromCartData = {
      contents: removeFromCartProducts,
      value: totalValue,
      currency: currency,
    };

    window.fbq('track', 'RemoveFromCart', removeFromCartData);
  }
};

import { bootstrap, optOut, optIn } from 'vue-gtag';
const configureTagManager = (hasConsent: boolean) => {
  if (hasConsent) {
    if (!window.gtag) {
      if (bootstrap) {
        // already bootstrapped?
        try {
          bootstrap().then(() => {
            optIn();
          });
        } catch (err) {}
      }
    }
  } else {
    try {
      optOut();
    } catch (err) {}
  }
};

export default function useAnalyticUtils() {
  return {
    trackPageView: trackPageView,
    trackPromotionView: trackPromotionView,
    trackBookingConfirmation: trackBookingConfirmation,
    trackAddRoomToBasket: trackAddRoomToBasket,
    trackRemoveRoomFromBasket: trackRemoveRoomFromBasket,
    configureTagManager: configureTagManager,
  };
}
